import { Fragment, useEffect, useState } from 'react';
import { getEmailSendLogs, type EmailSendLog } from '../../services/firebase';

interface EmailHistoryProps {
  /** Change this value to force a reload (e.g. after a successful send). */
  reloadKey?: number;
}

function formatSentAt(log: EmailSendLog): string {
  const date = log.sentAt?.toDate?.();
  if (!date) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function targetSummary(log: EmailSendLog): string {
  if (log.target === 'all') return 'All';
  const n = (log.target as { userIds?: string[] })?.userIds?.length ?? 0;
  return `${n} user${n === 1 ? '' : 's'}`;
}

export function EmailHistory({ reloadKey = 0 }: EmailHistoryProps) {
  const [logs, setLogs] = useState<EmailSendLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getEmailSendLogs(20)
      .then((rows) => {
        if (!cancelled) setLogs(rows);
      })
      .catch((err) => {
        console.error('Failed to load email logs:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load history');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (logs.length === 0) {
    return <p className="text-sm text-gray-500">No emails sent yet.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-2 pr-4 font-medium">Sent</th>
            <th className="py-2 pr-4 font-medium">Subject</th>
            <th className="py-2 pr-4 font-medium">Category</th>
            <th className="py-2 pr-4 font-medium">Target</th>
            <th className="py-2 pr-4 font-medium text-right">Queued</th>
            <th className="py-2 pr-4 font-medium text-right">Skipped</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <Fragment key={log.id}>
              <tr
                className="border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                onClick={() =>
                  setExpanded(expanded === log.id ? null : log.id)
                }
              >
                <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">
                  {formatSentAt(log)}
                </td>
                <td className="py-2 pr-4 text-gray-900">
                  {log.subject ?? '—'}
                  {log.isTest && (
                    <span className="ml-2 text-xs text-gray-500">(test)</span>
                  )}
                </td>
                <td className="py-2 pr-4 text-gray-700 capitalize">
                  {log.category ?? '—'}
                </td>
                <td className="py-2 pr-4 text-gray-700">
                  {targetSummary(log)}
                </td>
                <td className="py-2 pr-4 text-gray-900 text-right">
                  {log.queuedCount ?? 0}
                </td>
                <td className="py-2 pr-4 text-gray-500 text-right">
                  {(log.suppressedCount ?? 0) + (log.noEmailCount ?? 0)}
                </td>
              </tr>
              {expanded === log.id && (
                <tr className="border-b border-gray-100 bg-gray-50">
                  <td colSpan={6} className="py-3 px-4">
                    <p className="text-xs text-gray-500 mb-2">
                      Resolved {log.resolvedUserCount ?? 0} · no consent{' '}
                      {log.suppressedCount ?? 0} · no address{' '}
                      {log.noEmailCount ?? 0} · status {log.status ?? '—'}
                    </p>
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">
                      {log.body ?? ''}
                    </pre>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
